// Pure text helpers for the Markdown toolbar (unit-tested, no DOM)
export type FormatKind = 'h1' | 'h2' | 'h3' | 'b' | 'i' | 'u' | 's' | 'ul' | 'ol';

/** Applies a toolbar action to `value` for the selection [start, end) and returns the new text. */
export function applyFormat(value: string, start: number, end: number, kind: FormatKind): string {
  if (kind === 'b' || kind === 'i' || kind === 'u' || kind === 's') {
    const mark = { b: '**', i: '*', u: '++', s: '~~' }[kind];
    const text = value.slice(start, end) || { b: 'fett', i: 'kursiv', u: 'unterstrichen', s: 'durchgestrichen' }[kind];
    return value.slice(0, start) + mark + text + mark + value.slice(end);
  }
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  const nl = value.indexOf('\n', end);
  const lineEnd = nl === -1 ? value.length : nl;
  let n = 0;
  const block = value
    .slice(lineStart, lineEnd)
    .split('\n')
    .map(line => {
      if (kind === 'h1' || kind === 'h2' || kind === 'h3') {
        // same level again removes the heading, another level replaces it
        const mark = `${'#'.repeat(Number(kind[1]))} `;
        const plain = line.replace(/^#{1,3} /, '');
        return line.startsWith(mark) ? plain : mark + plain;
      }
      if (kind === 'ul') return line.startsWith('- ') ? line.slice(2) : `- ${line}`;
      return /^\d+\. /.test(line) ? line.replace(/^\d+\. /, '') : `${++n}. ${line}`;
    })
    .join('\n');
  return value.slice(0, lineStart) + block + value.slice(lineEnd);
}

export const insertAt = (value: string, start: number, end: number, text: string) =>
  value.slice(0, start) + text + value.slice(end);

/**
 * A typed address for a link: an e-mail address (with or without "mailto:") becomes a mailto: link – the same the
 * editor makes of a typed address on its own; otherwise "https://" is added when missing and only http(s) addresses
 * with a real host name (with a dot) are accepted; "tel:" + digits stays a phone link – never javascript: or an internal name.
 * (The page shows exactly these link types: see md.ts.)
 */
export function webAddress(raw: string): string | null {
  const t = raw.trim();
  if (!t || /\s/.test(t)) return null;
  const mail = /^(?:mailto:)?([A-Za-z0-9._+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,})$/i.exec(t);
  if (mail) return `mailto:${mail[1]}`;
  // a phone link (e.g. pasted from a web page) can be edited too: "tel:" + digits
  const tel = /^tel:(\+?[0-9][0-9./-]{2,30})$/i.exec(t);
  if (tel) return `tel:${tel[1].replace(/[./-]/g, '')}`;
  if (/^(javascript|data|vbscript|mailto|tel|file|blob):/i.test(t)) return null;
  // "tri-team.de:8080/x" has a port, not a scheme: only "xyz://" counts as written with a scheme
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(t) ? t : `https://${t}`;
  try {
    const u = new URL(withScheme);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (!u.hostname.includes('.') || u.username || u.password) return null;
    // brackets and quotes encoded: the address stays one piece in the saved Markdown "[text](address)"
    return u.href.replace(/[()'"[\]]/g, c => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
  } catch {
    return null;
  }
}

/**
 * Links in pasted HTML (from a web page, Word, a mail): web, e-mail and phone links stay (phone numbers as digits,
 * e-mail without ?subject=…); every other link (ftp:, relative "/seite", "#anker" …) becomes its plain text – the
 * page shows exactly these link types (md.ts), so nothing pasted ends up as raw Markdown.
 */
export function cleanPastedLinks(html: string): string {
  if (!/<a[\s/>]/i.test(html)) return html; // (also "<a/href=…>", which the browser reads as a link)
  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const a of Array.from(doc.querySelectorAll('a'))) {
    const href = (a.getAttribute('href') ?? '').trim();
    // (Word writes "tel:+49%20721%20123": decoded first, else "%20" would become digits)
    let tel: string;
    try {
      tel = /^tel:/i.test(href) ? decodeURIComponent(href.slice(4)) : '';
    } catch {
      tel = '';
    }
    const digits = tel.replace(/[^+0-9]/g, '');
    // web addresses checked like the link button (no login data in the address, brackets encoded)
    const target = /^https?:\/\/\S+$/i.test(href)
      ? webAddress(href)
      : /^mailto:/i.test(href)
        ? webAddress(href.replace(/\?.*$/, ''))
        : /^\+?[0-9]{3,20}$/.test(digits)
          ? `tel:${digits}`
          : null;
    if (target) a.setAttribute('href', target);
    else a.replaceWith(...Array.from(a.childNodes));
  }
  return doc.body.innerHTML;
}
